/**
 * Liked-vehicle compare selection — listing IDs only, scoped per client view (M7).
 */

export const BUYER_COMPARE_MAX = 4;
export const BUYER_COMPARE_MIN = 2;
export const BUYER_COMPARE_CHANGED_EVENT = "automap-buyer-compare-changed";

const STORAGE_KEY_PREFIX = "automap_liked_compare:";

function storageKey(slug) {
  return `${STORAGE_KEY_PREFIX}${String(slug || "").trim().toLowerCase()}`;
}

export function normalizeCompareVehicleIds(ids, max = BUYER_COMPARE_MAX) {
  if (!Array.isArray(ids)) return [];
  return [...new Set(ids.map((id) => String(id || "").trim()).filter(Boolean))].slice(0, max);
}

function readRawIds(slug) {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(storageKey(slug));
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeRawIds(slug, ids) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(storageKey(slug), JSON.stringify(normalizeCompareVehicleIds(ids)));
  } catch {
    // ignore quota errors
  }
}

export function emitBuyerCompareChanged(detail = {}) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(BUYER_COMPARE_CHANGED_EVENT, { detail }));
}

export function loadCompareVehicleIds(slug) {
  return normalizeCompareVehicleIds(readRawIds(slug));
}

export function saveCompareVehicleIds(slug, ids) {
  const next = normalizeCompareVehicleIds(ids);
  writeRawIds(slug, next);
  emitBuyerCompareChanged({ slug, compareIds: next });
  return next;
}

export function isVehicleInCompare(slug, listingId) {
  const id = String(listingId || "").trim();
  if (!id) return false;
  return loadCompareVehicleIds(slug).includes(id);
}

export function addVehicleToCompare(slug, listingId) {
  const id = String(listingId || "").trim();
  if (!id) return loadCompareVehicleIds(slug);
  const current = loadCompareVehicleIds(slug);
  if (current.includes(id)) return current;
  if (current.length >= BUYER_COMPARE_MAX) return current;
  return saveCompareVehicleIds(slug, [...current, id]);
}

export function removeVehicleFromCompare(slug, listingId) {
  const id = String(listingId || "").trim();
  if (!id) return loadCompareVehicleIds(slug);
  return saveCompareVehicleIds(
    slug,
    loadCompareVehicleIds(slug).filter((entry) => entry !== id)
  );
}

export function toggleVehicleCompare(slug, listingId) {
  const id = String(listingId || "").trim();
  if (!id) return { ids: loadCompareVehicleIds(slug), added: false };
  if (isVehicleInCompare(slug, id)) {
    return { ids: removeVehicleFromCompare(slug, id), added: false };
  }
  const before = loadCompareVehicleIds(slug);
  if (before.length >= BUYER_COMPARE_MAX) {
    return { ids: before, added: false, atMax: true };
  }
  return { ids: addVehicleToCompare(slug, id), added: true };
}

export function clearCompareVehicles(slug) {
  return saveCompareVehicleIds(slug, []);
}

export function filterCompareIdsToLiked(slug, likedIds) {
  const likedSet = new Set(normalizeCompareVehicleIds(likedIds, 100));
  return loadCompareVehicleIds(slug).filter((id) => likedSet.has(id));
}
